import React, { useCallback, useContext, useMemo } from "react";

import {
  AlternativesBestMatches,
  NoBestMatches,
} from "@is/components/BestMatches";
import {
  ADDITIONAL_OFFSET,
  PRODUCT_DETAILS_SCROLL_OFFSET,
} from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import ProductRow from "@is/features/ContentView/ProductRow";
import { useScreenOrientation } from "@is/hooks";
import { ContentItem, ContentItemType } from "@is/types";

import ContentWidgetItem from "../../ContentWidgetItem";
import ProductDetailsItem from "../../ProductDetailsItem";
import WidgetItem from "../../WidgetItem";

type Props = {
  item: ContentItem;
  itemIndex: number;
  searchDrawerHeight?: number;
  sectionId: string;
  handleShowMore?: () => void;
  setShowGoToBottomButton?: React.Dispatch<React.SetStateAction<boolean>>;
};

const ITEMS_LEFT_TO_SHOW_MORE = 4;

const useManageContentItemComponent = ({
  item,
  itemIndex,
  searchDrawerHeight = 0,
  sectionId,
  handleShowMore,
  setShowGoToBottomButton,
}: Props) => {
  const { intersectedItemsIds, updateViewState, sections } =
    useContext(ViewStateContext).content;

  const { isLandscapeView } = useScreenOrientation();

  const itemOffset = useMemo(() => {
    let offset = "0";
    let landscapeOffset = "0";

    if (item.type === ContentItemType.ProductRow) {
      offset = `-${searchDrawerHeight}px`;
      landscapeOffset = `-${ADDITIONAL_OFFSET}px`;
    } else if (item.type === ContentItemType.ProductDetailsItem) {
      offset =
        itemIndex === 0
          ? PRODUCT_DETAILS_SCROLL_OFFSET.FirstItem
          : PRODUCT_DETAILS_SCROLL_OFFSET.OtherItems;
      landscapeOffset = "0";
    }

    return isLandscapeView ? landscapeOffset : offset;
  }, [item, searchDrawerHeight, itemIndex, isLandscapeView]);

  let content = null;

  if (item.type === ContentItemType.ProductRow) {
    content = (
      <ProductRow
        row={item.content}
        sectionId={sectionId}
        contentItemId={item.id}
      />
    );
  }

  if (item.type === ContentItemType.ProductDetailsItem) {
    content = (
      <ProductDetailsItem
        item={item.content}
        itemIndex={itemIndex}
        contentItemId={item.id}
        sectionId={sectionId}
      />
    );
  }

  if (item.type === ContentItemType.WidgetItem) {
    content = <WidgetItem contentItemId={item.id} widget={item.content} />;
  }

  if (item.type === ContentItemType.ContentWidgetItem) {
    content = (
      <ContentWidgetItem
        widget={item.content}
        contentItemId={item.id}
        maxPageItemsLimit={item.content.items.length}
        widgetType={item.content.type}
        sectionId={sectionId}
      />
    );
  }

  const currentSection = useMemo(
    () => sections.find((section) => section.id === sectionId),
    [sections, sectionId]
  );

  const bestMatchMessage = useMemo(() => {
    if (item.type !== ContentItemType.ProductRow) {
      return null;
    }

    if (item.isBestMatch) {
      return null;
    }

    if (itemIndex === 0) {
      return <NoBestMatches />;
    }

    const items = currentSection?.items || [];
    const indexOfNotMatchedAllAttributes = items.findIndex((item) => {
      if (item.type !== ContentItemType.ProductRow) {
        return false;
      }

      return !item.isBestMatch;
    });

    if (itemIndex === indexOfNotMatchedAllAttributes) {
      return <AlternativesBestMatches />;
    }

    return null;
  }, [item, itemIndex, currentSection]);

  const handleIntersectedItemsIdsUpdate = useCallback(() => {
    const lastSection = sections[sections.length - 1];
    const lastSectionItemsIds = lastSection.items.map((item) => item.id);

    if (intersectedItemsIds.includes(item.id)) {
      return;
    }

    const newIntersectedItemsIds = [...intersectedItemsIds, item.id];

    updateViewState({
      intersectedItemsIds: newIntersectedItemsIds,
    });

    const lastSectionViewedItems = lastSection.items.filter((item) =>
      newIntersectedItemsIds.includes(item.id)
    );

    if (
      lastSection.items.length - lastSectionViewedItems.length ===
        ITEMS_LEFT_TO_SHOW_MORE &&
      lastSectionItemsIds.includes(item.id)
    ) {
      handleShowMore();
    }
  }, [handleShowMore, intersectedItemsIds, item, sections, updateViewState]);

  const isLastIntersectedItem = useMemo(() => {
    const filteredSections = sections.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => intersectedItemsIds.indexOf(item.id) !== -1
      ),
    }));

    return (
      filteredSections[filteredSections?.length - 1]?.items[
        filteredSections[filteredSections.length - 1]?.items?.length - 1
      ]?.id === item.id
    );
  }, [intersectedItemsIds, item, sections]);

  const handleLastIntersectedItem = useCallback(
    (isIntersecting: boolean) => {
      if (isLastIntersectedItem && !isIntersecting) {
        setShowGoToBottomButton(() => true);
      } else if (isLastIntersectedItem && isIntersecting) {
        setShowGoToBottomButton(() => false);
      }
    },
    [isLastIntersectedItem, setShowGoToBottomButton]
  );

  return {
    handleIntersectedItemsIdsUpdate,
    handleLastIntersectedItem,
    content,
    bestMatchMessage,
    itemOffset,
  };
};

export default useManageContentItemComponent;
